import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router';

import {
    BodyTextMedium,
    Button,
    ButtonGroup,
    ButtonGroupTestID,
    HeadingText20,
    HeadingText50 as StepTitle,
    pxToRem,
} from '@modernhealth/vital-components';

import styled from '@emotion/styled';

import { mediaQueries } from 'src/shared/styles/variables';
import { SingleSelectQuestion } from 'src/modules/assessments/components/SingleSelectQuestion';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';

import { Dispatch } from 'src/config/rootStore';
import { SOURCE_TYPES } from 'src/modules/user/models/user';
import { navigationService } from 'src/config/rootService';
import { CareNavigationRequestSource, CareNavigationRequestTopic } from 'src/generated/webapp_gql';

import { CareNavigationSteps } from '../components/CareNavigationSteps';
import { CareNavigationRequest } from '../models/benefits';
import { useIsCareNavEnabledUser } from '../hooks/useIsCareNavEnabledUser';

const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: pxToRem(500),
    marginTop: pxToRem(100),
    marginRight: 'auto',
    marginLeft: 'auto',
    [mediaQueries.sm]: {
        padding: '0.5rem',
    },
});

const ButtonGroupContainer = styled.div({
    marginTop: pxToRem(32),
    marginBottom: pxToRem(24),
});

const TitleContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: pxToRem(24),
    marginTop: pxToRem(32),
});

const FormContainer = styled.div({
    textAlign: 'center',
    width: '100%',
    [mediaQueries.sm]: {
        paddingBottom: 0,
    },
});

const CrisisElementsContainer = styled.div({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
});

const CrisisLinkContainer = styled.div({
    paddingLeft: pxToRem(12),
    cursor: 'pointer',
    fontColor: 'blue55',
});

export const CareNavigationTopicStep: React.FC = () => {
    const location = useLocation<{
        careNavigationRequest?: CareNavigationRequest;
        step?: string;
    }>();
    const { t } = useTranslation();
    const dispatch = useDispatch<Dispatch>();
    const [requestState, setRequestState] = useState<CareNavigationRequest>(
        location.state.careNavigationRequest ||
            ({
                source: '',
                topic: '',
                message: '',
                contactPreference: '',
            } as CareNavigationRequest),
    );

    const user = useSelector(getUserInformation);
    const { isUserEnabledFlow } = useIsCareNavEnabledUser();

    const options = [
        {
            answerText: t('benefits:care_navigation_topic_step.answer_one'),
            value: CareNavigationRequestTopic.NavigatingMh,
        },
        {
            answerText: t('benefits:care_navigation_topic_step.answer_two', {
                company: user.company,
            }),
            value: CareNavigationRequestTopic.CompanyBenefits,
        },
        {
            answerText: t('benefits:care_navigation_topic_step.answer_three'),
            value: CareNavigationRequestTopic.Other,
        },
    ];

    if (!isUserEnabledFlow) {
        navigationService.navigate('/dashboard');
    }

    useEffect(() => {
        dispatch.user.updateCrisisModalSource(SOURCE_TYPES.CALLBACK_SCREEN);
    }, [dispatch.user]);

    // If the source is empty, we assume it came from deep-link, so we set the topic and source
    if (location.state.step && location.state.step === '1') {
        setRequestState({
            ...(requestState as CareNavigationRequest),
            source: CareNavigationRequestSource.BenefitsPage,
        });
        delete location.state.step;
    } else {
        if (requestState.source === undefined || requestState.source === '') {
            setRequestState({
                ...(requestState as CareNavigationRequest),
                source: CareNavigationRequestSource.WellbeingReport,
                topic: CareNavigationRequestTopic.Other,
            });
        }
    }

    if (requestState.source === CareNavigationRequestSource.WellbeingReport) {
        navigationService.navigate('/care-navigation-contact-method', requestState);
    }

    const isContinueButtonDisabled = () => {
        return !(requestState.topic !== '');
    };

    const handleContinue = () => {
        navigationService.navigate('/care-navigation-details', requestState);
    };

    const handleBack = () => {
        if (requestState.source === CareNavigationRequestSource.BenefitsPage) {
            navigationService.navigate('/all-your-benefits');
        } else {
            navigationService.navigate('/dashboard');
        }
    };

    return (
        <Wrapper>
            <CareNavigationSteps step={1} />

            <TitleContainer>
                <StepTitle centered>{t('benefits:care_navigation_topic_step.question')}</StepTitle>
            </TitleContainer>

            <FormContainer>
                <SingleSelectQuestion
                    currentRate={requestState.topic as string}
                    onRateChange={value =>
                        setRequestState({
                            ...requestState,
                            topic: value as CareNavigationRequestTopic,
                        })
                    }
                    options={options}
                />

                <ButtonGroupContainer>
                    <ButtonGroup>
                        <Button
                            variant="secondary"
                            onClick={handleBack}
                            data-testid={ButtonGroupTestID.FirstButton}
                        >
                            {t('common.back')}
                        </Button>
                        <Button
                            variant="primary"
                            size="large"
                            onClick={handleContinue}
                            disabled={isContinueButtonDisabled()}
                            data-testid={ButtonGroupTestID.SecondButton}
                        >
                            {t('common.continue')}
                        </Button>
                    </ButtonGroup>
                </ButtonGroupContainer>

                {requestState.source !== CareNavigationRequestSource.ContactUs && (
                    <CrisisElementsContainer>
                        <BodyTextMedium>{t('shared:crisis_support_banner.message')}</BodyTextMedium>
                        <CrisisLinkContainer
                            onClick={() => {
                                dispatch.user.toogleShowCrisisModal();
                            }}
                        >
                            <HeadingText20 underline fontColor="blue55">
                                {t('benefits:care_navigation_topic_step.view_crisis_resources')}
                            </HeadingText20>
                        </CrisisLinkContainer>
                    </CrisisElementsContainer>
                )}
            </FormContainer>
        </Wrapper>
    );
};
